import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Stack, 
  Avatar, 
  IconButton,
  Divider,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { ArrowBack, Phone, ArrowForward, CalendarToday, Person, Payment, Schedule, Cancel } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import COLORS from '../../Utils/colors';
import { request } from '../../Utils/request';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';

interface BookingData {
  _id?: string;
  bookingId?: string;
  tripId?: string;
  title?: string;
  name?: string;
  destination?: string;
  files?: Array<{
    fileKey: string;
    fileType: string;
    featured: boolean;
    fileURL: string;
  }>;
  batches?: {
    from: string;
    to: string;
  };
  batchData?: {
    from: string;
    to: string;
  };
  amountPaid?: number;
  quantity?: number;
  user?: {
    _id: string;
    firstName: string;
    lastName: string;
    username: string;
    profilePhoto: string | null;
  };
  days?: number;
  pricePerPerson?: number;
  price?: number;
  userInfo?: any[];
  info?: any[];
  type?: string;
}

const BookingInfo: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  
  // Get booking data from navigation state
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [normalizedData, setNormalizedData] = useState<any>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  
  useEffect(() => {
    if (location.state?.bookingData) {
      const data = location.state.bookingData;
      setBookingData(data);
      
      // Normalize data from both sources (ReservationsTab and BookingDetails)
      const normalized = {
        ...data,
      // Handle title from different sources
      title: data.title || data.name || data.destination || 'Trip',
      // Handle batches data from different sources
      batches: data.batches || data.batchData || { from: new Date().toISOString(), to: new Date().toISOString() },
      // Handle userInfo from different sources
      userInfo: data.userInfo || data.info || [],
        // Handle quantity from different sources
        quantity: data.quantity || (data.userInfo ? data.userInfo.length : (data.info ? data.info.length : 0)),
        // Handle tripId from different sources
        tripId: data.tripId || data._id || '',
        // Handle bookingId from different sources
        bookingId: data.bookingId || data._id || '',
        // Handle price from different sources
        pricePerPerson: data.pricePerPerson || data.price || 0,
        // Handle days from different sources
        days: data.days || data.duration || 3
      };
      
      setNormalizedData(normalized);
    } 
  }, [location.state]);
  
  // Show loading or return early if no data
  if (!bookingData || !normalizedData) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        backgroundColor: '#F5F5F5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const calculateDays = (from: string, to: string) => {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const diffTime = Math.abs(toDate.getTime() - fromDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const days = calculateDays(normalizedData.batches.from || normalizedData.batches.date.from,normalizedData.batches.to || normalizedData.batches.date.to);
  const nights = days - 1;

  /**
   * Handle cancel booking
   * Makes API call to cancel the booking using the booking _id
   * Shows confirmation dialog before cancellation
   * Navigates back to previous page after successful cancellation
   */
  const handleCancelBooking = async () => {
    // Get the booking ID from _id
    const bookingId = bookingData?._id 
    console.log("bookingId", bookingId);
    
    if (!bookingId) {
      alert('Booking ID not found');
      return;
    }

    setIsCancelling(true);
    
    try {
      // API call to cancel booking endpoint
      await request(`/trips/booking/${bookingId}/cancel`, {
        method: 'POST',
        authorization: true,
        body: {
          "reason": "Cancelled by user"
        }
      });
      
      // Close dialog
      setCancelDialogOpen(false);
      
      // Show success message
      alert('Booking cancelled successfully');
      
      // Navigate back to previous page after successful cancellation
      navigate(-1);
    } catch (error: any) {
      console.error('Failed to cancel booking:', error);
      alert(error.message || 'Failed to cancel booking. Please try again.');
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: '#F5F5F5',
      pb: 4
    }}>
      {/* Header */}
      {/* <Box sx={{ 
        backgroundColor: 'white', 
        p: 2, 
        borderBottom: '1px solid #E0E0E0'
      }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            onClick={() => window.history.back()}
            sx={{
              backgroundColor: 'transparent',
              borderRadius: '50%',
              p: 1
            }}
          >
            <ArrowBack sx={{ color: 'black' }} />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#263238' }}>
            Booking info
          </Typography>
        </Stack>
      </Box> */}
      <TopNavBar title="Booking info" canGoBack={true} onBackClick={() => navigate(-1)} />

      {/* Booking ID Banner */}
      <Box sx={{ 
        backgroundColor: COLORS.leafGreen, 
        p: 2, 
        mx: 2, 
        mt: 2, 
        borderRadius: 1
      }}>
        <Typography sx={{ 
          color: '#2E7D32', 
          fontWeight: 'bold',
          fontSize: '1.1rem'
        }}>
          Booking Id: {normalizedData.bookingId.slice(-8) || '---'}
        </Typography>
      </Box>

      {/* Content Cards */}
      <Stack spacing={2} sx={{ p: 2 }}>
        {/* Travel Dates Card */}
        <Card variant="outlined" sx={{ 
          borderRadius: 2, 
          backgroundColor: 'white',
          borderColor: '#FFE0B2'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#757575', fontSize: '0.9rem' }}>
                Travel dates
              </Typography>
            </Stack>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#263238', mb: 0.5 }}>
              {formatDate(normalizedData.batches.from || normalizedData.batches.date.from)} - {formatDate(normalizedData.batches.to || normalizedData.batches.date.to)}
            </Typography>
            <Typography variant="body2" sx={{ color: '#757575' }}>
              {days} day{days > 1 ? 's' : ''} • {nights} night{nights > 1 ? 's' : ''}
            </Typography>
          </CardContent>
        </Card>

        {/* Travelers Card */}
        <Card variant="outlined" sx={{ 
          borderRadius: 2, 
          backgroundColor: 'white',
          borderColor: '#FFE0B2'
        }}>
          <Box sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <Person sx={{ color: COLORS.primary, fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: '#757575', fontSize: '0.9rem' }}>
                Traveler's
              </Typography>
            </Stack>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#263238', mb: 2 }}>
              {normalizedData.quantity} Person{normalizedData.quantity > 1 ? 's' : ''}
            </Typography>
            
            {/* Traveler entries */}
            <Stack spacing={2}>
             
              
              {normalizedData.userInfo.length > 0 && (
                <>
               
                  {normalizedData.userInfo.map((user: any, index: number) => (
                    <Stack key={index} direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{ 
                          width: 40, 
                          height: 40, 
                          backgroundColor: COLORS.sunriseCoral,
                          fontSize: '1rem'
                        }}>
                          {user.name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#263238' }}>
                            {user.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#757575' }}>
                            {user.age} • {user.gender} • {user.email}
                          </Typography>
                        </Box>
                      </Stack>
                      <IconButton sx={{ color: COLORS.primary }}>
                        <Phone />
                      </IconButton>
                    </Stack>
                  ))}
                </>
              )}
            </Stack>
          </Box>
        </Card>

        {/* Payment ID Card */}
        {/* <Card variant="outlined" sx={{ 
          borderRadius: 2, 
          backgroundColor: 'white',
          borderColor: '#FFE0B2'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
              <Payment sx={{ color: COLORS.primary, fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: '#757575', fontSize: '0.9rem' }}>
                Payment id
              </Typography>
            </Stack>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#263238' }}>
              {bookingData?._id || '---'}
            </Typography>
          </CardContent>
        </Card> */}

        {/* Booked On Card */}
        <Card variant="outlined" sx={{ 
          borderRadius: 2, 
          backgroundColor: 'white',
          borderColor: '#FFE0B2'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
              <Schedule sx={{ color: COLORS.primary, fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: '#757575', fontSize: '0.9rem' }}>
                Booked on
              </Typography>
            </Stack>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#263238', mb: 0.5 }}>
              {formatDate(normalizedData.batches.createdAt || normalizedData.createdAt)}
            </Typography>
            <Typography variant="body2" sx={{ color: '#757575' }}>
              {formatTime(normalizedData.batches.createdAt || normalizedData.createdAt)}
            </Typography>
          </CardContent>
        </Card>

        {/* Trip Itinerary Card */}
        <Card variant="outlined" sx={{ 
          borderRadius: 2, 
          backgroundColor: 'white',
          borderColor: '#FFE0B2'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="body2" sx={{ color: '#757575', fontSize: '0.9rem', mb: 1 }}>
                  Trip 
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: COLORS.tealBlue, 
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                  onClick={() => navigate(`/trips/${normalizedData.tripId}`,{  state: { tripData: normalizedData } })}
                >
                  Discover the {normalizedData.title}
                </Typography>
              </Box>
              <IconButton sx={{ color: COLORS.primary }}>
                <ArrowForward />
              </IconButton>
            </Stack>
          </CardContent>
        </Card>

        {/* Cancel Booking Button */}
        <Box sx={{ mt: 2, pb: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="error"
            startIcon={<Cancel />}
            onClick={() => setCancelDialogOpen(true)}
            sx={{
              borderRadius: 2,
              py: 1.5,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 'bold',
            }}
          >
            Cancel Booking
          </Button>
        </Box>
      </Stack>

      {/* Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !isCancelling && setCancelDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#263238' }}>
          Cancel Booking?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#757575' }}>
            Are you sure you want to cancel this booking? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            disabled={isCancelling}
            sx={{
              textTransform: 'none',
              color: '#757575',
              fontWeight: 'bold'
            }}
          >
            No, Keep Booking
          </Button>
          <Button
            onClick={handleCancelBooking}
            variant="contained"
            color="error"
            disabled={isCancelling}
            startIcon={isCancelling ? <CircularProgress size={20} color="inherit" /> : <Cancel />}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              px: 3
            }}
          >
            {isCancelling ? 'Cancelling...' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookingInfo;
